import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, MessageCircle } from "lucide-react";
import MindshareNavbar from "../components/MindshareNavbar";
import MindshareFooter from "../components/MindshareFooter";
import {
  useProfileInfo,
  useScoreGrowth,
  useTwitterAuth,
  useDiscordAuth,
  useTelegramAuth,
  useUserQuests,
} from "../hooks";
import { useGlobalWallet } from "../context/WalletContext";

const Profile = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("quests");
  const {
    address: globalAddress,
    isConnected,
    isInitialized,
    connect: globalConnect,
    formattedAddress: globalFormattedAddress,
  } = useGlobalWallet();

  // Redirect to mindshare if wallet is not connected (only after initialization)
  useEffect(() => {
    if (isInitialized && !isConnected) {
      navigate('/mindshare');
    }
  }, [isInitialized, isConnected, navigate]);

  // Fetch user profile info when wallet is connected
  const {
    profileData,
    loading: profileLoading,
    refetch: refetchProfile,
  } = useProfileInfo(globalAddress, { immediate: !!globalAddress });

  // Handle Twitter OAuth callback redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    if (token) {
      localStorage.setItem("auth_token", token);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (globalAddress) {
        refetchProfile();
      }
    }

    if (error) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [globalAddress, refetchProfile]);

  // Clear referral code after Twitter connection is confirmed
  useEffect(() => {
    if (profileData?.isTwitterConnected) {
      localStorage.removeItem('referralCode');
    }
  }, [profileData?.isTwitterConnected]);

  // Refetch profile on mount to catch redirects from OAuth (Discord/Telegram)
  useEffect(() => {
    if (globalAddress && refetchProfile) {
      refetchProfile();
    }
  }, []);

  // Fetch growth chart data when wallet is connected
  const {
    graphData,
    loading: scoreLoading,
  } = useScoreGrowth(globalAddress, { immediate: !!globalAddress });

  // Fetch user completed quests
  const {
    quests,
  } = useUserQuests(globalAddress, { immediate: !!globalAddress });

  // Fallback for disconnect scenarios
  const connect = globalAddress ? () => {} : globalConnect;

  // Twitter OAuth flow
  const {
    isAuthenticating,
    error: twitterError,
    initiateAuth: initiateTwitterAuthBase,
  } = useTwitterAuth({
    onSuccess: () => {},
    onError: () => {},
  });

  // Discord OAuth flow
  const {
    isAuthenticating: isDiscordAuthenticating,
    error: discordError,
    initiateAuth: initiateDiscordAuthBase,
  } = useDiscordAuth({
    onError: () => {},
  });

  // Telegram OAuth flow
  const {
    isAuthenticating: isTelegramAuthenticating,
    error: telegramError,
    initiateAuth: initiateTelegramAuthBase,
  } = useTelegramAuth({
    onError: () => {},
  });

  // Handle Link X click
  const handleLinkX = async () => {
    if (!isConnected) {
      alert("Please connect your wallet first");
      return;
    }
    await initiateTwitterAuthBase(globalAddress);
  };

  // Handle Link Discord click
  const handleLinkDiscord = async () => {
    if (!isConnected) {
      alert("Please connect your wallet first");
      return;
    }
    if (!profileData?.isTwitterConnected) {
      alert("Please connect X first");
      return;
    }
    await initiateDiscordAuthBase(globalAddress);
  };

  // Handle Link Telegram click
  const handleLinkTelegram = async () => {
    if (!isConnected) {
      alert("Please connect your wallet first");
      return;
    }
    if (!profileData?.isTwitterConnected) {
      alert("Please connect X first");
      return;
    }
    await initiateTelegramAuthBase(globalAddress);
  };

  // Helper: generate SVG path from backend graph points
  const generateChartPath = (data) => {
    if (!Array.isArray(data) || data.length < 2) return "";

    const scores = data.map(
      (p) => (typeof p.score === "number" ? p.score : parseFloat(p.score)) || 0,
    );
    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const range = maxScore - minScore || 1;

    const height = 80;
    const width = 200;
    const padding = 10;
    const usableHeight = height - padding - 0;

    return data.reduce((acc, point, i) => {
      const x = (i / (data.length - 1)) * width;
      const raw =
        (typeof point.score === "number"
          ? point.score
          : parseFloat(point.score)) || 0;
      const normalized = (raw - minScore) / range;
      const y = height - normalized * (usableHeight - padding) - padding;
      return i === 0 ? `M${x},${y}` : `${acc} L${x},${y}`;
    }, "");
  };

  // Multipliers data (Coming Soon)
  const multipliers = [
    { id: 1, title: "Staking Multiplier", active: false, rate: "0.25x", icon: "/stake.png" },
    { id: 2, title: "Social Multiplier", active: false, rate: "0.25x", icon: "/followers.png" },
    { id: 3, title: "NFT Multiplier", active: false, rate: "0.25x", icon: "/starDots.png" },
    { id: 4, title: "Governance Multiplier", active: false, rate: "0.25x", icon: "/governance.png" },
  ];

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#1B2464",
      }}
    >
      {/* Navigation Header with Gold Background */}
      <div 
        className="relative"
        style={{
          backgroundImage: "url(/ggbrBackgroundGold.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "320px",
          backdropFilter: "blur(150px)",
        }}
      >
        {/* Black Overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: "rgba(0, 0, 0, 0.66)",
          }}
        />
        
        {/* Smooth Gradient to Blue - Extended and Stronger */}
        <div 
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(27, 36, 100, 0.6) 50%, rgba(27, 36, 100, 1) 100%)",
          }}
        />

        {/* Navigation Header */}
        <div className="relative z-10 flex flex-col items-center justify-between h-full">
          <MindshareNavbar showBackToLeaderboard={true} />
          <h1 
            className="text-4xl md:text-5xl text-center"
            style={{ color: "white", fontWeight: 400, marginTop: "40px", marginBottom: "20px" }}
          >
            User <span style={{ color: "#D4AF37" }}>Profile</span>
          </h1>
        </div>
      </div>

      {/* Main Content with Smooth Transition */}
      <div className="flex justify-center px-4 md:px-0" style={{ background: "linear-gradient(180deg, rgba(27, 36, 100, 0.8) 0%, #1B2464 40%)" }}>
        <div className="w-full md:w-[91.5vw]">
          {/* Profile Section - Single Row */}
          <div className="relative z-[10] py-4 md:py-6">
            <div
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
              style={{
                background: "rgba(27, 36, 100, 0.4)",
                backdropFilter: "blur(10px)",
                padding: "16px",
              }}
            >
              {/* Left - Profile Picture and Details (1.5 columns) */}
              <div className="md:col-span-2 lg:col-span-1.5 flex flex-col sm:flex-row gap-4 md:gap-6 items-center sm:items-start">
                <div className="w-[120px] h-[140px] md:w-[180px] md:h-[200px] overflow-hidden flex-shrink-0">
                  {profileLoading ? (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <span className="text-2xl">⏳</span>
                    </div>
                  ) : profileData?.twitterProfilePictureUrl ? (
                    <img
                      src={profileData.twitterProfilePictureUrl.replace('_normal', '_400x400')}
                      alt="Profile"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <span className="text-4xl">👨‍💻</span>
                    </div>
                  )}
                </div>

                {/* Profile Details */}
                <div className="flex-1 text-center sm:text-left">
                  <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 mb-4 md:mb-6">
                    <h2 className="text-xl md:text-3xl text-white font-medium">
                      {profileData?.twitterName || "User"}
                    </h2>
                    <span
                      className="px-2 py-1 text-xs font-medium"
                      style={{
                        background: "#D4AF37",
                        color: "#1B2464",
                      }}
                    >
                      Rank #{profileData?.rank || "N/A"}
                    </span>
                  </div>

                  {/* Stats Rows - Icon, Label, Value per row */}
                  <div className="flex flex-col gap-2 md:gap-4">
                    <div className="flex items-center justify-center sm:justify-start gap-2 md:gap-3">
                      <img src="/trending.svg" alt="" className="w-4 h-4 md:w-5 md:h-5" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span className="text-white text-sm md:text-base uppercase font-medium">Mindshare</span>
                      <div className="text-white text-lg md:text-2xl font-semibold">
                        {profileData?.mindshare
                          ? typeof profileData.mindshare === "number"
                            ? profileData.mindshare.toFixed(2)
                            : profileData.mindshare
                          : "0.00"}
                      </div>
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-2 md:gap-3">
                      <img src="/zap.svg" alt="" className="w-4 h-4 md:w-5 md:h-5" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span className="text-white text-sm md:text-base uppercase font-medium">
                        {profileData?.xpBoostFactor > 1 ? "XP Boost" : "Multiplier"}
                      </span>
                      <div className="text-white text-lg md:text-2xl font-semibold">
                        {profileData?.xpBoostFactor > 1
                          ? `${profileData.xpBoostFactor.toFixed(1)}x`
                          : profileData?.multiplier
                            ? `${profileData.multiplier.toFixed(1)}x`
                            : "1.0x"}
                      </div>
                      {profileData?.xpBoostFactor > 1 && (
                        <span className="text-[10px] md:text-xs uppercase font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2 py-0.5">
                          Active · on new XP
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-2 md:gap-3">
                      <img src="/users-round.svg" alt="" className="w-4 h-4 md:w-5 md:h-5" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span className="text-white text-sm md:text-base uppercase font-medium">Followers</span>
                      <div className="text-white text-lg md:text-2xl font-semibold">
                        {profileData?.twitterFollowers?.toLocaleString() || "0"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wallet Card */}
              <div
                className="p-4 md:p-5 h-full flex flex-col justify-between"
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <img src="/wallet.svg" alt="" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) invert(1)" }} />
                  <span className="text-white font-semibold text-sm md:text-base">Wallet</span>
                </div>
                <p className="text-xs md:text-sm text-white/80 mb-2 md:mb-3">
                  {isConnected ? "Linked" : "Connect wallet"}
                </p>
                {isConnected ? (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-white text-xs truncate">{globalFormattedAddress}</span>
                    <span
                      className="px-1.5 py-0.5 text-xs whitespace-nowrap"
                      style={{ background: "white", color: "#1B2464", fontWeight: 600 }}
                    >
                      Active
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={connect}
                    className="w-full py-1 md:py-2 text-xs md:text-sm font-medium"
                    style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid white" }}
                  >
                    Connect
                  </button>
                )}
              </div>

              {/* Combined Social Accounts Card */}
              <div
                className="p-4 md:p-5 h-full flex flex-col"
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                {/* X Account Section */}
                <div className="mb-3">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-lg font-bold" style={{ color: "white" }}>𝕏</span>
                    <span className="text-white font-semibold text-xs md:text-sm">Account</span>
                  </div>
                  {profileData?.isTwitterConnected ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white text-xs truncate">{profileData?.twitterUsername || "Connected"}</span>
                      <span
                        className="px-1.5 py-0.5 text-xs whitespace-nowrap"
                        style={{ background: "white", color: "#1B2464", fontWeight: 600 }}
                      >
                        Active
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={handleLinkX}
                      disabled={isAuthenticating}
                      className="w-full py-1 text-xs font-medium disabled:opacity-50"
                      style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid white" }}
                    >
                      {isAuthenticating ? "..." : "Link"}
                    </button>
                  )}
                </div>

                {/* Divider */}
                <div className="h-px my-2" style={{ background: "rgba(255, 255, 255, 0.2)" }}></div>

                {/* Discord Section */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <MessageCircle size={16} color="white" />
                    <span className="text-white font-semibold text-xs md:text-sm">Discord</span>
                  </div>
                  {profileData?.isDiscordConnected ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white text-xs truncate">{profileData?.discordUsername || "Connected"}</span>
                      <span
                        className="px-1.5 py-0.5 text-xs whitespace-nowrap"
                        style={{ background: "white", color: "#1B2464", fontWeight: 600 }}
                      >
                        Ready
                      </span>
                    </div>
                  ) : (
                    <button
                      onClick={handleLinkDiscord}
                      disabled={isDiscordAuthenticating || !profileData?.isTwitterConnected}
                      className="w-full py-1 text-xs font-medium disabled:opacity-50"
                      style={{ background: "rgba(255,255,255,0.2)", color: "white", border: "1px solid white" }}
                    >
                      {isDiscordAuthenticating ? "..." : "Link"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Activity Modules & Performance Section */}
      <div style={{ background: "#1B2464" }}>
        <div className="flex justify-center py-4 px-4 md:px-0">
          <div className="w-full md:w-[91.5vw] grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Activity Modules */}
            <div 
              className="p-4 md:p-6 flex flex-col"
              style={{ background: "rgba(255, 255, 255, 0.05)" }}
            >
              <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                <img src="/activity.svg" alt="" className="w-5 h-5 md:w-6 md:h-6" style={{ filter: "brightness(0) invert(1)" }} />
                <span className="text-white font-medium text-lg md:text-xl">Activity Modules</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                {/* Quests Module */}
                <button
                  className="flex-1 px-3 md:px-4 py-2 md:py-3 flex items-center justify-center gap-2 md:gap-3 rounded"
                  style={{ 
                    background: "#FFFFFF",
                    color: "#1B2464",
                    border: "1px solid #E5E7EB",
                  }}
                  onClick={() => navigate('/mindshare')}
                >
                  <img src="/git-pull-request-create-arrow.svg" alt="" className="w-4 h-4 md:w-5 md:h-5" style={{ filter: "brightness(0) saturate(100%) invert(12%) sepia(18%) hue-rotate(190deg)" }} />
                  <span className="text-base md:text-lg font-medium" style={{ color: "#1B2464" }}>Quests</span>
                </button>

                {/* Twitter Module */}
                <button
                  className="flex-1 px-3 md:px-4 py-2 md:py-3 flex items-center justify-center gap-2 md:gap-3 rounded"
                  style={{ 
                    background: profileData?.isTwitterConnected ? "#575C6E66" : "#FFFFFF",
                    color: profileData?.isTwitterConnected ? "#FFFFFF" : "#1B2464",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <span className="text-2xl md:text-3xl font-bold" style={{ color: profileData?.isTwitterConnected ? "#FFFFFF" : "#1B2464" }}>𝕏</span>
                  <span className="text-base md:text-lg font-medium" style={{ color: profileData?.isTwitterConnected ? "#FFFFFF" : "#1B2464" }}>
                    {profileData?.isTwitterConnected ? profileData?.twitterUsername : "Not Connected"}
                  </span>
                </button>
              </div>
            </div>

            {/* Your Performance Card */}
            <div className="p-4 md:p-6" style={{ background: "rgba(255, 255, 255, 0.05)" }}>
              <div className="flex items-center gap-2 mb-3 md:mb-4">
                <img src="/chart-spline.svg" alt="" className="w-4 h-4" style={{ filter: "brightness(0) invert(1) saturate(200%) hue-rotate(45deg)" }} />
                <span className="text-white font-medium text-base md:text-lg">Your Performance</span>
              </div>

              {/* Desktop: 3 columns (labels | values | chart), Mobile: stacked with chart below */}
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
                {/* Labels column */}
                <div className="hidden md:flex flex-1 flex-col space-y-3">
                  <div className="flex items-center gap-2 text-gray-400 text-base">
                    <img src="/trending.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                    <span>Mindshare</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 text-base">
                    <img src="/arrow-up-narrow-wide.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                    <span>Rank</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-400 text-base">
                    <img src="/star.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                    <span>Score</span>
                  </div>
                </div>

                {/* Values column (desktop) */}
                <div className="hidden md:flex flex-1 flex-col space-y-3">
                  <div className="text-[#D4AF37] font-medium text-base">
                    {profileData?.mindshare
                      ? typeof profileData.mindshare === "number"
                        ? `${profileData.mindshare.toFixed(2)}%`
                        : `${profileData.mindshare}%`
                      : "0.00%"}
                  </div>
                  <div className="text-white font-medium text-base">{profileData?.rank || "N/A"}</div>
                  <div className="text-white font-medium text-base">{profileData?.score?.toLocaleString() || "0"}</div>
                </div>

                {/* Mobile: inline rows */}
                <div className="md:hidden w-full space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <img src="/trending.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span>Mindshare</span>
                    </div>
                    <span className="text-[#D4AF37] font-medium text-sm">
                      {profileData?.mindshare
                        ? typeof profileData.mindshare === "number"
                          ? `${profileData.mindshare.toFixed(2)}%`
                          : `${profileData.mindshare}%`
                        : "0.00%"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <img src="/arrow-up-narrow-wide.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span>Rank</span>
                    </div>
                    <span className="text-white font-medium text-sm">{profileData?.rank || "N/A"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <img src="/star.svg" alt="" className="w-4 h-4" style={{ flexShrink: 0, filter: "brightness(0) invert(1)" }} />
                      <span>Score</span>
                    </div>
                    <span className="text-white font-medium text-sm">{profileData?.score?.toLocaleString() || "0"}</span>
                  </div>
                </div>

                {/* Chart column (desktop only - inline with metrics) */}
                <div className="hidden md:block flex-1 h-20 relative">
                  {graphData && graphData.length > 1 ? (
                    (() => {
                      const linePath = generateChartPath(graphData);
                      const areaPath = linePath + " L200,80 L0,80 Z";
                      return (
                        <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="profileChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                              <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
                              <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <path d={linePath} fill="none" stroke="#D4AF37" strokeWidth="2" />
                          <path d={areaPath} fill="url(#profileChartGradient)" />
                        </svg>
                      );
                    })()
                  ) : (
                    <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="profileChartGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M0,70 L40,65 L80,50 L120,40 L160,25 L200,10" fill="none" stroke="#D4AF37" strokeWidth="2" />
                      <path d="M0,70 L40,65 L80,50 L120,40 L160,25 L200,10 L200,80 L0,80 Z" fill="url(#profileChartGradient)" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Chart (mobile only - below metrics) */}
              <div className="md:hidden mt-4 h-16 relative">
                {graphData && graphData.length > 1 ? (
                  (() => {
                    const linePath = generateChartPath(graphData);
                    const areaPath = linePath + " L200,80 L0,80 Z";
                    return (
                      <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="profileChartGradientMobile" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d={linePath} fill="none" stroke="#D4AF37" strokeWidth="2" />
                        <path d={areaPath} fill="url(#profileChartGradientMobile)" />
                      </svg>
                    );
                  })()
                ) : (
                  <svg className="w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="profileChartGradientMobile" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,70 L40,65 L80,50 L120,40 L160,25 L200,10" fill="none" stroke="#D4AF37" strokeWidth="2" />
                    <path d="M0,70 L40,65 L80,50 L120,40 L160,25 L200,10 L200,80 L0,80 Z" fill="url(#profileChartGradientMobile)" />
                  </svg>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="flex justify-center py-4 px-4 md:px-0">
          <div 
            className="w-full md:w-[50%]"
            style={{ 
              background: "linear-gradient(134.45deg, rgba(99, 108, 167, 0.6) 0.47%, rgba(27, 36, 100, 0.6) 99.53%)",
              backdropFilter: "blur(10px)",
              padding: "16px",
              border: "0.5px solid #FFFFFF80",
            }}
          >
            <div className="flex gap-0">
              <button
                className="flex-1 text-xs md:text-sm font-medium transition-colors"
                style={{
                  padding: "4px 6px",
                  lineHeight: "1.2",
                  color: activeTab === "quests" ? "#D4AF37" : "#9CA3AF",
                }}
                onClick={() => setActiveTab("quests")}
              >
                COMPLETED QUESTS
              </button>
              <button
                className="flex-1 text-xs md:text-sm font-medium cursor-not-allowed"
                style={{
                  padding: "4px 6px",
                  lineHeight: "1.2",
                  color: "#6B7280",
                }}
                disabled
              >
                EARNING HISTORY
              </button>
              <button
                className="flex-1 text-xs md:text-sm font-medium cursor-not-allowed"
                style={{
                  padding: "4px 6px",
                  lineHeight: "1.2",
                  color: "#6B7280",
                }}
                disabled
              >
                DAO VOTES
              </button>
            </div>
          </div>
        </div>

        {/* Completed Quests Grid */}
        <div className="flex justify-center py-4 md:py-8 px-4 md:px-0">
          <div className="w-full md:w-[91.5vw]">
            {quests && quests.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-8">
                {quests.slice(0, 6).map((quest, index) => {
                  const questId = quest._id || quest.id;
                  return (
                    <div
                      key={questId || index}
                      className="p-4 md:p-8 flex flex-col items-center justify-center text-center"
                      style={{
                        background: "linear-gradient(134.45deg, rgba(99, 108, 167, 0.6) 0.47%, rgba(27, 36, 100, 0.6) 99.53%)",
                        backdropFilter: "blur(5px)",
                        minHeight: "180px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <Check size={36} className="md:w-12 md:h-12" style={{ color: "#D4AF37" }} />
                      </div>
                      <div className="text-lg md:text-2xl" style={{ color: "white" }}>
                        {quest.title || quest.name || "Quest"}
                      </div>
                      <div className="w-full flex items-center justify-between">
                        <span className="text-xs md:text-sm" style={{ color: "#9CA3AF", textTransform: "uppercase" }}>Points</span>
                        <span className="text-base md:text-lg" style={{ color: "#D4AF37", fontWeight: 600 }}>{quest.xpAmount || "0"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-300 text-lg">No completed quests yet.</p>
                <p className="text-gray-400 text-base mt-2">Start by joining our community!</p>
              </div>
            )}
          </div>
        </div>

        {/* Multipliers Section (Coming Soon) */}
        <div className="flex justify-center py-8 md:py-12 px-4 md:px-0">
          <div className="w-full md:w-[91.5vw] relative">
            {/* Blurred Quest Cards Grid */}
            <div 
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-8"
              style={{ filter: "blur(6px)", opacity: 0.4, pointerEvents: "none" }}
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="p-4 md:p-8 flex flex-col items-center justify-center text-center"
                  style={{
                    background: "linear-gradient(134.45deg, rgba(99, 108, 167, 0.6) 0.47%, rgba(27, 36, 100, 0.6) 99.53%)",
                    backdropFilter: "blur(5px)",
                    minHeight: "180px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  <div>
                    <Check size={36} className="md:w-12 md:h-12" style={{ color: "#D4AF37" }} />
                  </div>
                  <div className="text-lg md:text-2xl" style={{ color: "white" }}>
                    Upcoming Quest
                  </div>
                  <div className="w-full flex items-center justify-between">
                    <span className="text-xs md:text-sm" style={{ color: "#9CA3AF", textTransform: "uppercase" }}>Points</span>
                    <span className="text-base md:text-lg" style={{ color: "#D4AF37", fontWeight: 600 }}>0</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 flex items-center justify-center">
              <h3 
                className="text-3xl md:text-4xl tracking-wider"
                style={{ color: "#FFFFFF", fontWeight: 500 }}
              >
                Coming Soon
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <MindshareFooter />
    </div>
  );
};

export default Profile;
